import React, { useState, useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router-dom";
import "../profile.css";

// Components
import LazyImage from "../../../../components/LazyImage/LazyImage";
import SharePopup from "../SharePopup";
import PhotoUploader from "../../../../components/PhotoUploader";
import CreatorEditProfile from "../CreatorEditProfile";
import ProfileActions from "../ProfileActions";
import FollowersListModal from "../../../Modals/FollowersList";
import ProfilePictureModal from "../../../Modals/ProfilePictureModal";

// Actions
import { fetchTotalTip } from "../../../../shared/actions/creatorActions";

// Assets and utilities
import { nFormatter, isUserLoggedIn, formatUsdAmountDisplayCents } from "../../../../shared/utils/function.js";
import { resolveVatRate } from "../../../../shared/utils/paymentUtil";
import { getLocalItem } from "../../../../shared/utils/config/localStorage";
import {
  ROLE,
  UPLOAD_TYPE,
  DEFAULT_USER_AVATAR,
  defaultProfileBanner,
  CALL_PRICING_MODEL_PER_MINUTE,
  CALL_PRICING_MODEL_FLAT_RATE,
} from "../../../../shared/constants/common";

import GridDropdown from "../../../../features/cards/user/gridDropdown";
import throneLogoWhite from "../../../../assets/img/throne_white.png";

// Constants for image protection
const IMAGE_PROTECTION_PROPS = {
  onContextMenu: (e) => e.preventDefault(),
  draggable: false
};
const maxLength = 100;

const UserProfileHeader = ({
  // Props
  isLogging,
  userRole,
  isOwnProfile = false,
  creatorLoading = false,
  creatorInfo,
  onSubscriptionSuccess,
  // Redux state
  user
}) => {
  const isLoggedIn = isUserLoggedIn();
  const { creatorUsername } = useParams();
  const canEdit = isOwnProfile && isLogging;
  const isNormalUser = !isLoggedIn || userRole === ROLE.USER;
  const profilePicture = creatorInfo?.profile_picture || DEFAULT_USER_AVATAR;
  const profileBanner = creatorInfo?.profile_banner || defaultProfileBanner;
  const isEmailConfirmed = getLocalItem("account_verified") || false;
  const subscribed = creatorInfo?.subscribed || false;
  // Check if voice/video features should be displayed
  const showVoiceCall = creatorInfo?.voice?.enabled;
  const showVideoCall = creatorInfo?.video?.enabled;
  const freePostsCount = Number(creatorInfo?.freePictures) || 0;

  const [bioExpanded, setBioExpanded] = useState(false);
  const [bioText, setBioText] = useState(() => creatorInfo?.profile_description || "");
  const [totalTipAmount, setTotalTipAmount] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showFollowersListModal, setShowFollowersListModal] = useState(false);
  const [showProfilePictureModal, setShowProfilePictureModal] = useState(false);
  const [vatPercentage, setVatPercentage] = useState(0);

  // Toggle body class when followers list modal opens/closes
  useEffect(() => {
    if (showFollowersListModal) {
      document.body.classList.add('hide_header_open_popup');
    } else {
      document.body.classList.remove('hide_header_open_popup');
    }
    return () => {
      document.body.classList.remove('hide_header_open_popup');
    };
  }, [showFollowersListModal]);

  useEffect(() => {
    setBioText(creatorInfo?.profile_description || "");
  }, [user, creatorInfo]);

  // Fetch total tips when user is logged in and viewing own profile
  useEffect(() => {
    let cancelled = false;

    const loadTotalTip = async () => {
      if (!isLogging || !isOwnProfile) {
        return;
      }

      const result = await fetchTotalTip();
      if (!cancelled && result.success && result.data) {
        setTotalTipAmount(result.data.totalTip || 0);
      }
    };

    loadTotalTip();

    return () => {
      cancelled = true;
    };
  }, [isLogging, isOwnProfile]);

  // Effect to fetch VAT percentage
  useEffect(() => {
    const fetchVatPercentage = async () => {
      const vatPercentage = await resolveVatRate(user);
      setVatPercentage(vatPercentage);
    };
    fetchVatPercentage();
  }, [user]);

  const toggleBioExpansion = useCallback(() => {
    setBioExpanded(prev => !prev);
  }, []);

  const handleDropdownToggle = useCallback((id) => {
    setOpenDropdownId(prevId => prevId === id ? null : id);
  }, []);

  // Determine voice call display based on pricing model
  const getVoiceCallDisplay = () => {
    if (!showVoiceCall) return null;

    if (creatorInfo?.voice?.pricingModel === 'per_minute') {
      return {
        amount: creatorInfo?.voice?.perMinute?.amount,
        duration: Math.round(creatorInfo?.voice?.perMinute?.minCallMinutess),
        type: 'per_minute'
      };
    } else {
      return {
        amount: creatorInfo?.voice?.flatRate?.amount,
        duration: Math.round(creatorInfo?.voice?.flatRate?.durationMinutes),
        type: 'flat_rate'
      };
    }
  };

  // Determine video call display based on pricing model
  const getVideoCallDisplay = () => {
    if (!showVideoCall) return null;

    if (creatorInfo?.video?.pricingModel === 'per_minute') {
      return {
        amount: creatorInfo?.video?.perMinute?.amount,
        duration: Math.round(creatorInfo?.video?.perMinute?.minCallMinutess),
        type: 'per_minute'
      };
    } else {
      return {
        amount: creatorInfo?.video?.flatRate?.amount,
        duration: Math.round(creatorInfo?.video?.flatRate?.durationMinutes),
        type: 'flat_rate'
      };
    }
  };

  const voiceCallData = getVoiceCallDisplay();
  const videoCallData = getVideoCallDisplay();

  // Check if creator data is available — only show full skeleton on first load, not on background refetch
  const hasCreatorData = Boolean(creatorInfo?.username);
  const shouldShowLoading = !hasCreatorData;

  // Loading Skeleton Component
  if (shouldShowLoading) {
    return (
      <div className="profile-header-loading">
        {/* Creator Header Gallery - Loading */}
        <section className="creator-header-gallery">
          <div className="header-gallery-container">
            <div className="header-grid">
              <div className="header-grid-item">
                <div className="skeleton-banner"></div>
                <div className="header-item-overlay"></div>
              </div>
            </div>
            <div className="header-main-overlay"></div>
          </div>
        </section>

        {/* Profile Details Section - Loading */}
        <section className="profile-details-section">
          <div className="profile-details-container">
            <div className="profile-top-section">
              <div className="profile-avatar-section">
                <div className="skeleton-avatar"></div>
              </div>
            </div>

            <div className="creator-info-page">
              <div className="username-section">
                <div className="skeleton-username"></div>
                <div className="skeleton-free-content"></div>
              </div>

              <div className="creator-stats">
                <div className="skeleton-stat"></div>
                <div className="skeleton-stat"></div>
                <div className="skeleton-stat"></div>
              </div>
            </div>

            <div className="bio-section">
              <div className="skeleton-bio-line"></div>
              <div className="skeleton-bio-line"></div>
              <div className="skeleton-bio-line short"></div>
            </div>
          </div>
        </section>

        {/* CTA Section - Loading */}
        <section className="cta-section">
          <div className="skeleton-tips-display"></div>
        </section>
      </div>
    );
  }

  const followersListModalHandler = () => {
    if (creatorInfo?.subscribers > 0 && isOwnProfile && isLoggedIn) {
      setShowFollowersListModal(true);
    }
  }

  return (
    <div className="profile-header-loaded">
      {/* Creator Header Gallery */}
      <section className="creator-header-gallery">
        <div className="header-gallery-container">
          <div className="header-grid">
            <div className="header-grid-item">
              <LazyImage
                {...IMAGE_PROTECTION_PROPS}
                className="header-image image-protected"
                src={profileBanner || defaultProfileBanner}
                alt="Creator header background"
              />
              <div className="header-item-overlay"></div>
              <div className="header-item-hover"></div>
            </div>
          </div>
          <div className="header-main-overlay"></div>
          <div className="header_main_actions">
            <SharePopup
              userName={isOwnProfile ? creatorInfo?.username || '' : creatorUsername}
            />

            {canEdit && (
              <PhotoUploader
                uploadType={UPLOAD_TYPE.BANNER}
                buttonLabel="Edit"
                buttonIcon="fa-pen-to-square"
                className="header-edit-btn cm_menu_start_right"
                menuPosition="bottom-left"
                uploadingText="Uploading Banner..."
              />
            )}

            {isNormalUser && (
              <GridDropdown
                className="cm_media_grid_dropdown"
                iconClass="fa-ellipsis-vertical"
                id={creatorInfo?._id || ''}
                creatorId={creatorInfo?._id || ''}
                isOpen={openDropdownId === creatorInfo?._id}
                onToggle={handleDropdownToggle}
                isShowImpersonation={true}
                isSinglePost={false}
                postId={null}
              />
            )}
          </div>
        </div>
      </section>

      {/* Profile Details Section */}
      <section className="profile-details-section">
        <div className="profile-details-container">
          <div className="profile-top-section">
            <div className="profile-avatar-section">
              <button
                type="button"
                className="profile-avatar-button"
                onClick={() => setShowProfilePictureModal(true)}
                aria-label="View profile picture"
              >
                <LazyImage
                  {...IMAGE_PROTECTION_PROPS}
                  className="profile-avatar image-protected"
                  src={profilePicture || DEFAULT_USER_AVATAR}
                  alt="Creator Avatar"
                />
              </button>
              {canEdit && (
                <PhotoUploader
                  uploadType={UPLOAD_TYPE.PICTURE}
                  menuPosition="bottom-left"
                  buttonIcon="fa-pen-to-square"
                  showButton={false}
                  className="modern-side-menu-edit-badge"
                  uploadingText="Uploading Profile Picture..."
                >
                  <i className="fa-solid fa-pen-to-square"></i>
                </PhotoUploader>
              )}
            </div>

            {canEdit && (userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT) && (
              <CreatorEditProfile
                className="profile-edit-profile"
                isLogging={isLogging}
                isOwnProfile={isOwnProfile}
                userRole={userRole}
                creatorInfo={creatorInfo}
              />
            )}

            {/* Profile actions for normal users (message, follow/subscribe buttons) */}
            {isNormalUser && (
              <div className="profile-actions-section">
                <ProfileActions
                  creatorInfo={creatorInfo}
                  subscribed={subscribed}
                  vatPercentage={vatPercentage}
                  subscriptionLoading={creatorLoading}
                  isEmailConfirmed={isEmailConfirmed}
                  loggedIn={isLoggedIn}
                  promotionData={null}
                  promotionLoading={false}
                  onSubscriptionSuccess={onSubscriptionSuccess}
                />
              </div>
            )}
          </div>

          <div className="creator-info-page">
            <div className="username-section">
              <h3 className="cm_creator_username mt-0">
                @{creatorInfo?.username}
                {creatorInfo?.rol === ROLE.CREATOR_SUB && creatorInfo?.trending?.isTrending && creatorInfo?.trending?.isTrending === true && (
                  <i className="fa-solid fa-fire"></i>
                )}
              </h3>
              {freePostsCount > 0 && (
                <p className="free-content-preview">
                  Browse {freePostsCount} free posts
                </p>
              )}
            </div>

            <div className="creator-stats">
              {(creatorInfo?.subscribers || 0) > 0 && (
                <div
                  className="stat-item hover-scale cursor-pointer group"
                  onClick={() => followersListModalHandler()}
                >
                  <i className="fa-solid fa-users stat-icon"></i>
                  <span className="stat-text">
                    {nFormatter(creatorInfo?.subscribers || 0, 1)}
                  </span>
                </div>
              )}

              <div className="stat-item hover-scale cursor-pointer group">
                <i className="fa-solid fa-phone stat-icon"></i>
                <span className="stat-text">
                  {voiceCallData ?
                    <>
                      ${voiceCallData.amount}{voiceCallData.type === 'per_minute' ? '/min' : ''} {" "}
                      {voiceCallData.duration > 1 ?
                        voiceCallData.type === 'per_minute' ?
                          `(${voiceCallData.duration} min req)` :
                          `(per ${voiceCallData.duration} min)` :
                        ''}
                    </>
                    : `$${CALL_PRICING_MODEL_PER_MINUTE.AMOUNT}/min`
                  }
                </span>
              </div>

              <div className="stat-item hover-scale cursor-pointer group">
                <i className="fa-solid fa-video stat-icon"></i>
                <span className="stat-text">
                  {videoCallData ?
                    <>
                      ${videoCallData.amount}{videoCallData.type === 'per_minute' ? '/min' : ''} {" "}
                      {videoCallData.duration > 1 ?
                        videoCallData.type === 'per_minute' ?
                          `(${videoCallData.duration} min req)` :
                          `(per ${videoCallData.duration} min)`
                        : ''
                      }
                    </>
                    : `$${CALL_PRICING_MODEL_FLAT_RATE.AMOUNT}  (per ${CALL_PRICING_MODEL_FLAT_RATE.DURATION} min)`
                  }
                </span>
              </div>
            </div>
          </div>

          {bioText && (
            <div className="bio-section">
              <pre className="bio-text">
                {(() => {
                  if (bioText?.length <= maxLength || bioExpanded) {
                    return bioText;
                  }

                  return bioText?.substring(0, maxLength) + "...";
                })()}
              </pre>
              {bioText &&
                bioText.trim().length > 100 && (
                  <button
                    className="show-more-btn"
                    onClick={toggleBioExpansion}
                  >
                    {bioExpanded ? "show less" : "show more"}
                  </button>
                )}
            </div>
          )}
        </div>
      </section>

      {/* DM Prompt Section */}
      {(creatorInfo?.pinMessage || creatorInfo?.wishlistUrl || creatorInfo?.throneWishlistUrl) && (
        <section className="dm-prompt-section">
          {creatorInfo?.pinMessage && (
            <div className="dm-prompt-card">
              <button
                type="button"
                className="dm-avatar-button"
                onClick={() => setShowProfilePictureModal(true)}
                aria-label="View profile picture"
              >
                <img
                  {...IMAGE_PROTECTION_PROPS}
                  src={profilePicture}
                  alt="Creator Avatar"
                  className="dm-avatar image-protected"
                />
              </button>
              <div className="dm-content">
                <p className="dm-message">{creatorInfo?.pinMessage}</p>
                <p className="dm-label">Pinned Message</p>
              </div>
            </div>
          )}
          {(creatorInfo?.wishlistUrl || creatorInfo?.throneWishlistUrl) && (
            <div className="wishlist-section">
              {creatorInfo?.wishlistUrl && (
                <div className="wishlist-item hover-scale cursor-pointer group">
                  <div className="wishlist-icon">
                    <i className="fa-brands fa-amazon wishlist-icon-text"></i>
                  </div>
                  <a
                    href={creatorInfo?.wishlistUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wishlist-text"
                  >
                    Wish List
                  </a>
                </div>
              )}
              {creatorInfo?.throneWishlistUrl && (
                <div className="wishlist-item wishlist-item-throne hover-scale cursor-pointer group">
                  <div className="wishlist-icon">
                    <img
                      src={throneLogoWhite}
                      alt=""
                      className="throne-wishlist-logo"
                    />
                  </div>
                  <a
                    href={creatorInfo?.throneWishlistUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="wishlist-text"
                  >
                    Throne
                  </a>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* CTA Section */}
      {(userRole === ROLE.CREATOR_SUB || userRole === ROLE.CREATOR_CONTENT) && (
        <section className="cta-section">
          <div className="tips-display gap-0">
            <span className="tips-label">Total tips</span>
            <div className="tips-icon">
              <i className="fa-solid fa-sack-dollar tips-icon-text"></i>
            </div>
            <span className="tips-amount">
              {totalTipAmount === null
                ? "—"
                : `$${formatUsdAmountDisplayCents(totalTipAmount)}`}
            </span>
          </div>
        </section>
      )}

      <FollowersListModal
        isOpen={showFollowersListModal}
        onClose={() => setShowFollowersListModal(false)}
        creatorId={creatorInfo?._id}
        creatorRole={creatorInfo?.rol}
        title={creatorInfo?.rol === ROLE.CREATOR_SUB ? "Subscribers" : "Followers"}
        totalCount={creatorInfo?.subscribers}
      />

      <ProfilePictureModal
        isOpen={showProfilePictureModal}
        onClose={() => setShowProfilePictureModal(false)}
        profilePictureUrl={profilePicture}
        creatorInfo={creatorInfo}
        subscribed={subscribed}
      />
    </div>
  );
};

const mapStateToProps = (state) => {
  return {
    user: state.user?.user,
  };
};

export default connect(mapStateToProps)(UserProfileHeader);

